import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Globe from "react-globe.gl";
import * as THREE from "three";
import {
  ArrowForwardRounded,
  CloseRounded,
  ExploreOutlined,
  LanguageRounded,
  LocationOnOutlined,
  RestartAltRounded,
  TouchAppOutlined,
  ZoomInRounded,
  ZoomOutRounded,
} from "@mui/icons-material";
import "./globalCommerceGlobe.css";

// Visual map assets from the existing three-globe example set.
// These represent the Earth, NOT real-time Universe Inventory customer activity.
const IMAGE_ROOT = "https://cdn.jsdelivr.net/npm/three-globe@2.45.2/example/img";
const NIGHT_TEXTURE = `${IMAGE_ROOT}/earth-night.jpg`;
const DAY_TEXTURE = `${IMAGE_ROOT}/earth-blue-marble.jpg`;

const INITIAL_VIEW = { lat: 13, lng: -18, altitude: 2.35 };
const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";

// Illustrative geographic markers only. Do not label them as live customers.
const LOCATIONS = [
  { id: "lagos", name: "Lagos", country: "Nigeria", lat: 6.5244, lng: 3.3792 },
  { id: "abuja", name: "Abuja", country: "Nigeria", lat: 9.0765, lng: 7.3986 },
  { id: "london", name: "London", country: "United Kingdom", lat: 51.5074, lng: -0.1278 },
  { id: "dubai", name: "Dubai", country: "United Arab Emirates", lat: 25.2048, lng: 55.2708 },
  { id: "new-york", name: "New York", country: "United States", lat: 40.7128, lng: -74.006 },
  { id: "singapore", name: "Singapore", country: "Singapore", lat: 1.3521, lng: 103.8198 },
  { id: "tokyo", name: "Tokyo", country: "Japan", lat: 35.6762, lng: 139.6503 },
  { id: "sydney", name: "Sydney", country: "Australia", lat: -33.8688, lng: 151.2093 },
  { id: "sao-paulo", name: "São Paulo", country: "Brazil", lat: -23.5505, lng: -46.6333 },
  { id: "johannesburg", name: "Johannesburg", country: "South Africa", lat: -26.2041, lng: 28.0473 },
];

const ROUTES = [
  { startLat: 6.5244, startLng: 3.3792, endLat: 51.5074, endLng: -0.1278 },
  { startLat: 6.5244, startLng: 3.3792, endLat: 40.7128, endLng: -74.006 },
  { startLat: 25.2048, startLng: 55.2708, endLat: 1.3521, endLng: 103.8198 },
  { startLat: 51.5074, startLng: -0.1278, endLat: 25.2048, endLng: 55.2708 },
  { startLat: 35.6762, startLng: 139.6503, endLat: -33.8688, endLng: 151.2093 },
];

const BENEFITS = [
  { title: "Inventory in sync", detail: "Know what is available across branches." },
  { title: "Sales with clarity", detail: "Keep transactions and operations connected." },
  { title: "Built for expansion", detail: "Grow from one location to many." },
];

const vertexShader = `
  varying vec2 vUvEarth;
  varying vec3 vSurfaceNormal;
  varying vec3 vViewDirection;
  void main() {
    vUvEarth = uv;
    vec4 worldPos = modelMatrix * vec4(position, 1.0);
    vSurfaceNormal = normalize(mat3(modelMatrix) * normal);
    vViewDirection = cameraPosition - worldPos.xyz;
    gl_Position = projectionMatrix * viewMatrix * worldPos;
  }
`;

// The night side is illuminated by real satellite city-light imagery;
// the soft daylight crescent preserves recognizable land and oceans.
const fragmentShader = `
  uniform sampler2D dayTexture;
  uniform sampler2D nightTexture;
  uniform vec3 sunlightDirection;
  varying vec2 vUvEarth;
  varying vec3 vSurfaceNormal;
  varying vec3 vViewDirection;
  void main() {
    vec3 normalDirection = normalize(vSurfaceNormal);
    float daylight = dot(normalDirection, normalize(sunlightDirection));
    float dayBlend = smoothstep(-0.16, 0.45, daylight);
    vec3 dayMap = texture2D(dayTexture, vUvEarth).rgb;
    vec3 nightMap = texture2D(nightTexture, vUvEarth).rgb;
    vec3 deepOcean = vec3(0.007, 0.018, 0.047);
    vec3 nightSurface = max(nightMap * vec3(1.23, 1.18, 1.12), deepOcean);
    vec3 daySurface = dayMap * (0.57 + 0.4 * max(daylight, 0.0));
    vec3 earth = mix(nightSurface, daySurface, dayBlend);
    float rim = pow(1.0 - max(dot(normalDirection, normalize(vViewDirection)), 0.0), 4.5);
    earth += vec3(0.014, 0.105, 0.24) * rim;
    gl_FragColor = vec4(earth, 1.0);
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }
`;

function useMotionPreference() {
  const [reduceMotion, setReduceMotion] = useState(() =>
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia(REDUCED_MOTION).matches
  );

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const media = window.matchMedia(REDUCED_MOTION);
    const onChange = (event) => setReduceMotion(event.matches);
    if (media.addEventListener) media.addEventListener("change", onChange);
    else media.addListener(onChange);
    return () => {
      if (media.removeEventListener) media.removeEventListener("change", onChange);
      else media.removeListener(onChange);
    };
  }, []);
  return reduceMotion;
}

export default function GlobalCommerceGlobe() {
  const globeRef = useRef(null);
  const canvasContainerRef = useRef(null);
  const reduceMotion = useMotionPreference();
  const [dimensions, setDimensions] = useState({ width: 640, height: 630 });
  const [isReady, setIsReady] = useState(false);
  const [material, setMaterial] = useState(null);
  const [assetFailed, setAssetFailed] = useState(false);
  const [webGLSupported] = useState(() => {
    if (typeof document === "undefined" || typeof window === "undefined") return false;
    try {
      const testCanvas = document.createElement("canvas");
      return !!(window.WebGLRenderingContext &&
        (testCanvas.getContext("webgl") || testCanvas.getContext("experimental-webgl")));
    } catch (_) {
      return false;
    }
  });
  const [interacting, setInteracting] = useState(false);
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [showPlaces, setShowPlaces] = useState(false);

  const compact = dimensions.width < 540;
  const visibleLocations = useMemo(
    () => (compact ? LOCATIONS.filter((point) => ["lagos", "london", "dubai", "new-york", "tokyo"].includes(point.id)) : LOCATIONS),
    [compact]
  );

  useEffect(() => {
    if (!canvasContainerRef.current) return undefined;
    const container = canvasContainerRef.current;
    const measure = () => {
      const width = Math.max(270, Math.round(container.getBoundingClientRect().width));
      const height = Math.max(300, Math.round(container.getBoundingClientRect().height));
      setDimensions((previous) =>
        previous.width === width && previous.height === height ? previous : { width, height }
      );
    };
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let disposed = false;
    const loader = new THREE.TextureLoader();
    let textures = [];
    let createdMaterial = null;
    Promise.allSettled([loader.loadAsync(DAY_TEXTURE), loader.loadAsync(NIGHT_TEXTURE)])
      .then((results) => {
        textures = results.filter((result) => result.status === "fulfilled").map((result) => result.value);
        if (disposed || results.some((result) => result.status === "rejected")) {
          textures.forEach((texture) => texture.dispose());
          textures = [];
          if (!disposed) setAssetFailed(true);
          return;
        }
        const [day, night] = textures;
        day.colorSpace = THREE.SRGBColorSpace;
        night.colorSpace = THREE.SRGBColorSpace;
        createdMaterial = new THREE.ShaderMaterial({
          uniforms: {
            dayTexture: { value: day },
            nightTexture: { value: night },
            sunlightDirection: { value: new THREE.Vector3(0.65, 0.2, -0.73).normalize() },
          },
          vertexShader,
          fragmentShader,
        });
        setMaterial(createdMaterial);
      });
    return () => {
      disposed = true;
      if (createdMaterial) createdMaterial.dispose();
      textures.forEach((texture) => texture.dispose());
    };
  }, []);

  const configureControls = useCallback(() => {
    const globe = globeRef.current;
    if (!globe) return;
    const controls = globe.controls();
    if (!controls) return;
    controls.enablePan = false;
    controls.enableZoom = !compact;
    controls.enableRotate = true;
    controls.enableDamping = true;
    controls.dampingFactor = 0.07;
    controls.autoRotate = !reduceMotion && !selectedPoint && !interacting && !hoveredPoint;
    controls.autoRotateSpeed = 0.22;
    controls.minDistance = 135;
    controls.maxDistance = 380;
    const renderer = globe.renderer();
    if (renderer) renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, compact ? 1.4 : 1.8));
  }, [compact, reduceMotion, selectedPoint, interacting, hoveredPoint]);

  useEffect(() => {
    if (isReady) configureControls();
  }, [isReady, configureControls]);

  const handleReady = useCallback(() => {
    setIsReady(true);
    globeRef.current?.pointOfView(INITIAL_VIEW, 0);
  }, []);

  const focusLocation = useCallback((point) => {
    if (!point) return;
    setSelectedPoint(point);
    setShowPlaces(false);
    globeRef.current?.pointOfView({ lat: point.lat, lng: point.lng, altitude: 1.85 }, 1050);
  }, []);

  const resetView = useCallback(() => {
    setSelectedPoint(null);
    setHoveredPoint(null);
    globeRef.current?.pointOfView(INITIAL_VIEW, reduceMotion ? 0 : 850);
  }, [reduceMotion]);

  const adjustZoom = useCallback((delta) => {
    const globe = globeRef.current;
    if (!globe) return;
    const view = globe.pointOfView();
    globe.pointOfView({
      lat: view.lat,
      lng: view.lng,
      altitude: Math.min(3.7, Math.max(1.4, view.altitude + delta)),
    }, reduceMotion ? 0 : 450);
  }, [reduceMotion]);

  const closeCard = useCallback(() => setSelectedPoint(null), []);

  return (
    <section className="global-globe-section" aria-labelledby="global-globe-headline">
      <div className="global-globe-stars" aria-hidden="true" />
      <div className="global-globe-shell">
        <div className="global-globe-topline">
          <span><span className="global-globe-beacon" /> UNIVERSE INVENTORY / WORLDVIEW</span>
          <span className="global-globe-topline-aside">A WORLD OF CONNECTED OPERATIONS</span>
        </div>

        <div className="global-globe-main">
          <div className="global-globe-visual">
            <div className="global-globe-aura" aria-hidden="true" />
            <div className="global-globe-lens-ring" aria-hidden="true" />
            <div className="global-globe-render" ref={canvasContainerRef}
              onPointerDown={() => setInteracting(true)}
              onPointerUp={() => setInteracting(false)}
              onPointerCancel={() => setInteracting(false)}
              onPointerLeave={() => setInteracting(false)}
            >
              {webGLSupported ? (
                <Globe
                  ref={globeRef}
                  width={dimensions.width}
                  height={dimensions.height}
                  backgroundColor="rgba(0,0,0,0)"
                  globeImageUrl={NIGHT_TEXTURE}
                  globeMaterial={material || undefined}
                  showAtmosphere
                  atmosphereColor="#2989ff"
                  atmosphereAltitude={0.16}
                  onGlobeReady={handleReady}
                  pointsData={visibleLocations}
                  pointLat="lat"
                  pointLng="lng"
                  pointAltitude={(point) => selectedPoint?.id === point.id ? 0.055 : 0.018}
                  pointRadius={(point) => selectedPoint?.id === point.id ? 0.48 : 0.22}
                  pointColor={(point) => selectedPoint?.id === point.id ? "#ffffff" : "#56d9ff"}
                  pointResolution={12}
                  pointLabel={(point) => `<div class="global-globe-tooltip"><strong>${point.name}</strong><span>${point.country}</span><small>Illustrative location</small></div>`}
                  onPointHover={(point) => setHoveredPoint(point || null)}
                  onPointClick={focusLocation}
                  arcsData={compact ? ROUTES.slice(0, 2) : ROUTES}
                  arcStartLat="startLat"
                  arcStartLng="startLng"
                  arcEndLat="endLat"
                  arcEndLng="endLng"
                  arcColor={() => ["rgba(67,217,255,0.04)", "rgba(75,149,255,0.64)"]}
                  arcAltitudeAutoScale={0.27}
                  arcStroke={0.42}
                  arcDashLength={0.23}
                  arcDashGap={0.85}
                  arcDashAnimateTime={reduceMotion ? 0 : 4500}
                  arcsTransitionDuration={300}
                  ringsData={reduceMotion ? [] : visibleLocations.slice(0, compact ? 2 : 4)}
                  ringLat="lat"
                  ringLng="lng"
                  ringColor={() => ["rgba(73,205,255,0.50)", "rgba(73,205,255,0)"]}
                  ringMaxRadius={2.2}
                  ringPropagationSpeed={1.15}
                  ringRepeatPeriod={2650}
                />
              ) : (
                <div className="global-globe-static">
                  <img src={NIGHT_TEXTURE} alt="Satellite imagery showing Earth's nighttime lights" />
                  <p>3D exploration is unavailable on this device. Explore our platform using the links beside the globe.</p>
                </div>
              )}
            </div>
            <div className="global-globe-visual-grain" aria-hidden="true" />
            <div className="global-globe-left-label" aria-hidden="true"><span className="global-globe-label-dash" /> EARTH / NIGHT VIEW</div>
            <div className="global-globe-marker"><span>01</span><span>GLOBAL VIEW</span></div>

            {selectedPoint && (
              <div className="global-globe-place-card" role="status">
                <div className="global-globe-place-heading">
                  <span><LocationOnOutlined fontSize="small"/> LOCATION</span>
                  <button type="button" onClick={closeCard} aria-label="Dismiss location details"><CloseRounded fontSize="small"/></button>
                </div>
                <strong>{selectedPoint.name}</strong>
                <p>{selectedPoint.country}</p>
                <div className="global-globe-place-coords">{selectedPoint.lat.toFixed(3)}° / {selectedPoint.lng.toFixed(3)}°</div>
                <small>Illustrative global location — not a live customer record.</small>
              </div>
            )}
            <div className="global-globe-controls" aria-label="Globe controls">
              <button type="button" onClick={resetView} disabled={!isReady || !webGLSupported} aria-label="Reset globe view" title="Reset view"><RestartAltRounded/></button>
              <span aria-hidden="true" />
              <button type="button" onClick={() => adjustZoom(-0.35)} disabled={!isReady || !webGLSupported} aria-label="Zoom in" title="Zoom in"><ZoomInRounded/></button>
              <button type="button" onClick={() => adjustZoom(0.35)} disabled={!isReady || !webGLSupported} aria-label="Zoom out" title="Zoom out"><ZoomOutRounded/></button>
            </div>
            <div className="global-globe-interaction-hint"><TouchAppOutlined fontSize="small"/> {interacting ? "Exploring Earth" : "Drag to explore"}</div>
          </div>

          <div className="global-globe-copy">
            <div className="global-globe-copy-kicker"><LanguageRounded fontSize="small"/> WORLDWIDE POSSIBILITIES</div>
            <h2 id="global-globe-headline">Your business.<br/><em>Beyond boundaries.</em></h2>
            <p className="global-globe-intro">From your first sale to a growing network of branches, Universe Inventory connects the people, products and decisions that move your business forward.</p>
            <div className="global-globe-cta-row">
              <Link className="global-globe-primary-cta" to="/company/features">Explore Universe Inventory <ArrowForwardRounded/></Link>
              <Link className="global-globe-secondary-cta" to="/pricing">View plans <ArrowForwardRounded/></Link>
            </div>
            <div className="global-globe-divider" />
            <div className="global-globe-caption-row">
              <div className="global-globe-caption-symbol"><ExploreOutlined/></div>
              <div>
                <strong>A connected perspective</strong>
                <p>Discover how stock, sales, reports and locations come together in one platform.</p>
              </div>
            </div>
            <button type="button" className="global-globe-locations-button" onClick={() => setShowPlaces((value) => !value)} aria-expanded={showPlaces} aria-controls="global-globe-locations">
              <LocationOnOutlined fontSize="small"/> Explore illustrated locations <span>{showPlaces ? "−" : "+"}</span>
            </button>
            {showPlaces && (
              <div id="global-globe-locations" className="global-globe-places-list">
                {LOCATIONS.map((point) => (
                  <button type="button" onClick={() => focusLocation(point)} key={point.id}>
                    {point.name}<span>{point.country} ↗</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="global-globe-bottom">
          <div className="global-globe-benefits">
            {BENEFITS.map(({ title, detail }, index) => (
              <div key={title} className="global-globe-benefit">
                <span className="global-globe-benefit-num">0{index + 1} /</span>
                <div><strong>{title}</strong><p>{detail}</p></div>
              </div>
            ))}
          </div>
          <p className="global-globe-disclaimer">Earth imagery and geographic connections are illustrative, not live platform telemetry.</p>
          {assetFailed && <p className="global-globe-image-notice">Enhanced globe textures are unavailable. The standard nighttime view will be used.</p>}
        </div>
      </div>
    </section>
  );
}
